import { Box, Grid, Tab, Tabs, Typography } from '@mui/material';
import { styled } from '@mui/system';
import { useEffect, useState } from 'react';
import { fetchUserFacilities } from '../../../../redux/slices/transports';
import { useDispatch, useSelector } from '../../../../redux/store';
import { useScopeCheck } from '../../../../reusable-components/scopes';
import { TRANSPORT_ADMIN, TRANSPORT_COORDINATOR, TRANSPORT_NURSE } from '../constants';
import Locations from './Locations';
import OutsourceCompanies from './OutsourceCompanies';
import RestrictedDates from './RestrictedDates';
import Vehicles from './Vehicles';

const TAB_VALUES = {
  OUTSOURCE_COMPANIES: 'outsource-companies',
  VEHICLES: 'vehicles',
  RESTRICTED_DATES: 'restricted-dates',
  LOCATIONS: 'locations',
};

const CustomTab = styled((props) => <Tab {...props} />)(({ selected }) => ({
  flex: 1,
  minWidth: '160px',
  minHeight: '43px',
  height: '43px',
  maxHeight: '43px',
  borderBottom: 'none',
  marginTop: '5px',
  marginLeft: '4px',
  marginRight: '4px',
  borderLeft: 'none',
  borderRight: 'none',
  borderTop: 'none',
  color: '#444',
  borderRadius: '5px 5px 0px 0px',
  backgroundColor: selected ? '#fff' : '#f4f6f8',
  boxShadow: selected ? '0px 2px 4px 1px rgba(0, 0, 0, 0.2)' : '0px 1px 2px rgba(0, 0, 0, 0.1)',
  transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: '#fff',
    color: '#000',
    borderBottom: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    borderTop: 'none',
    boxShadow: '0px 2px 4px 1px rgba(0, 0, 0, 0.2)',
  },
  '& .MuiTab-wrapper': {
    flexDirection: 'column',
    alignItems: 'center',
  },
}));

export default function Settings() {
  const dispatch = useDispatch();
  const facilities = useSelector((state) => state.transports?.data?.facilities ?? []);
  const viewAllTabs = useScopeCheck([TRANSPORT_ADMIN, TRANSPORT_COORDINATOR], false, true);
  const viewRestrictedDatesTab = useScopeCheck([TRANSPORT_NURSE], false, true);

  const defaultTab = viewAllTabs
    ? TAB_VALUES.OUTSOURCE_COMPANIES
    : viewRestrictedDatesTab
    ? TAB_VALUES.RESTRICTED_DATES
    : null;

  const [tab, setTab] = useState(defaultTab);

  useEffect(() => {
    if (!facilities.length) {
      dispatch(fetchUserFacilities());
    }
  }, [dispatch, facilities.length]);

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
  };

  const renderTabContent = () => {
    switch (tab) {
      case TAB_VALUES.VEHICLES:
        return viewAllTabs ? <Vehicles /> : null;
      case TAB_VALUES.RESTRICTED_DATES:
        return viewAllTabs || viewRestrictedDatesTab ? <RestrictedDates /> : null;
      case TAB_VALUES.LOCATIONS:
        return viewAllTabs ? <Locations /> : null;
      case TAB_VALUES.OUTSOURCE_COMPANIES:
      default:
        return viewAllTabs ? <OutsourceCompanies /> : null;
    }
  };

  return (
    <Box>
      <Grid container direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Tabs
            value={tab || false}
            onChange={handleTabChange}
            indicatorColor="transparent"
            sx={{
              borderRadius: '4px 4px 0px 0px',
            }}
          >
            {viewAllTabs && (
              <CustomTab
                value={TAB_VALUES.OUTSOURCE_COMPANIES}
                label={
                  <>
                    <Box>
                      <Typography variant="subtitle1" color="success.contrastText" component="span">
                        Outsource Companies
                      </Typography>
                    </Box>
                  </>
                }
              />
            )}
            {viewAllTabs && (
              <CustomTab
                value={TAB_VALUES.VEHICLES}
                label={
                  <>
                    <Box>
                      <Typography variant="subtitle1" color="success.contrastText" component="span">
                        Vehicles
                      </Typography>
                    </Box>
                  </>
                }
              />
            )}
            {(viewRestrictedDatesTab || viewAllTabs) && (
              <CustomTab
                value={TAB_VALUES.RESTRICTED_DATES}
                label={
                  <>
                    <Box>
                      <Typography variant="subtitle1" color="success.contrastText" component="span">
                        Restricted Dates
                      </Typography>
                    </Box>
                  </>
                }
              />
            )}
            {viewAllTabs && (
              <CustomTab
                value={TAB_VALUES.LOCATIONS}
                label={
                  <>
                    <Box>
                      <Typography variant="subtitle1" color="success.contrastText" component="span">
                        Locations
                      </Typography>
                    </Box>
                  </>
                }
              />
            )}
          </Tabs>
        </Box>
      </Grid>

      {renderTabContent()}
    </Box>
  );
}
